import Notification from '../../shared/components/Notification/index';
import { NotificationTypes } from '../../enums/notificationTypes';
import axiosInstance from '../../interceptor/axiosInstance';
import { AxiosError } from 'axios';
import { UseMutationOptions, UseQueryOptions, useQueryClient } from '@tanstack/react-query';
import { authServiceMessages } from './auth.service.constants';
import { MutationKeys } from '../../enums/mutationKeys';
import { LocalStorageKeys } from '../../enums/localStorageKeys';
import { useAuth } from '../../context/AuthContext';
import { ApiRoutes } from '../../routes/routeConstants/apiRoutes';
import { generatePath } from 'react-router-dom';
import { QueryKeys } from '../../enums/queryKeys';
import { NotificationKeys } from '../../enums/notificationKeys';
import { PlacementPosition } from '../../enums/placementEnums';

const getAxiosErrorMessage = (error: unknown): string | undefined => {
  const axiosError = error as AxiosError<{ message?: string }>;
  return axiosError?.response?.data?.message || axiosError?.message;
};

const AuthService = () => {
  const { setAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  const loginUser = (): UseMutationOptions<any, Error, { email: string; password: string }> => ({
    mutationKey: [MutationKeys.AUTH_LOGIN],
    mutationFn: async ({ email, password }) => {
      const payload = {
        user: {
          email,
          password,
        },
      };
      const { data } = await axiosInstance.post(ApiRoutes.LOGIN, payload);
      localStorage.setItem(LocalStorageKeys.AUTH_TOKEN, data?.accessToken);
      localStorage.setItem(LocalStorageKeys.USER, JSON.stringify(data?.user));
      setAuthenticated(data?.user);
    },
    onSuccess: () => {
      Notification({
        message: authServiceMessages.LOGIN.SUCCESS,
        description: '',
        type: NotificationTypes.SUCCESS,
        key: NotificationKeys.LOGIN_SUCCESS,
      });
    },
    onError: (error) => {
      Notification({
        message: authServiceMessages.LOGIN.ERROR,
        description: getAxiosErrorMessage(error),
        type: NotificationTypes.ERROR,
        key: NotificationKeys.LOGIN_ERROR,
      });
    },
  });

  const requestPasswordReset = (): UseMutationOptions<any, Error, { email: string }> => ({
    mutationKey: [MutationKeys.AUTH_REQUEST_RESET],
    mutationFn: async ({ email }) => {
      const payload = {
        user: {
          email,
        },
      };
      const { data } = await axiosInstance.post(ApiRoutes.FORGOT_PASSWORD, payload);
      return data;
    },
    onError: (error) => {
      Notification({
        message: authServiceMessages.REQUEST_PASSWORD_RESET.ERROR,
        description: getAxiosErrorMessage(error),
        type: NotificationTypes.ERROR,
      });
    },
  });

  const verifyResetLink = (
    uuid: string,
    isAuthenticated: boolean,
  ): UseQueryOptions<{ message: string; user: { id: string; name: string } }, Error> => ({
    queryKey: [QueryKeys.AUTH_VERIFY_RESET, uuid],
    queryFn: async () => {
      const { data } = await axiosInstance.get(generatePath(ApiRoutes.RESET_PASSWORD, { uuid }));
      return data;
    },
    enabled: !isAuthenticated && Boolean(uuid),
    retry: false,
    staleTime: Infinity,
  });

  const resetPassword = (uuid: string): UseMutationOptions<any, Error, { newPassword: string }> => ({
    mutationKey: [MutationKeys.AUTH_SET_PASSWORD],
    mutationFn: async ({ newPassword }) => {
      const payload = {
        user: {
          newPassword,
        },
      };
      const { data } = await axiosInstance.post(generatePath(ApiRoutes.RESET_PASSWORD, { uuid }), payload);
      return data;
    },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: [QueryKeys.AUTH_VERIFY_RESET, uuid] });
      Notification({
        message: authServiceMessages.RESET_PASSWORD.SUCCESS,
        description: '',
        type: NotificationTypes.SUCCESS,
        placement: PlacementPosition.TOP,
      });
    },
    onError: (error) => {
      Notification({
        message: authServiceMessages.RESET_PASSWORD.ERROR,
        description: getAxiosErrorMessage(error),
        type: NotificationTypes.ERROR,
      });
    },
  });

  const cancelResetPassword = (uuid: string): UseMutationOptions<any, Error, any> => ({
    mutationKey: [MutationKeys.AUTH_CANCEL_RESET],
    mutationFn: async () => {
      const payload = { action: 'cancel' };
      const { data } = await axiosInstance.post(generatePath(ApiRoutes.RESET_PASSWORD, { uuid }), payload);
      return data;
    },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: [QueryKeys.AUTH_VERIFY_RESET, uuid] });
    },
    onError: (error) => {
      Notification({
        message: authServiceMessages.CANCEL_RESET_PASSWORD.ERROR,
        description: getAxiosErrorMessage(error),
        type: NotificationTypes.ERROR,
      });
    },
  });

  return {
    loginUser,
    requestPasswordReset,
    verifyResetLink,
    resetPassword,
    cancelResetPassword,
  };
};

export default AuthService;
